import React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useCrowdVoltStatus, useEntitlement } from '../hooks/useApi';
import { FlashtoolsEntitlement } from '../types';
import { AppShell } from './components/AppShell';
import { EmptyState } from './components/EmptyState';
import { Button } from '../components/base-components';
import { V2FeatureGate } from './components/V2FeatureGate';
import { V2_BILLING_BYPASS_PATHS, v2Path } from './nav';
import {
  DROPS_GATE,
  SEAT_LANDING_ROUTE,
  V2_DEFAULT_ROUTE,
  WAITLIST_GATE,
  isSeatVisiblePath,
} from './routes';
import { useSeatSession } from './useSeatSession';
import {
  AccountsScreen,
  BillingScreen,
  DiceSettingsScreen,
  DiceSignupScreen,
  DiceSuccessScreen,
  DropsScreen,
  ListingsScreen,
  NotificationsScreen,
  ProfileScreen,
  SeatsScreen,
  SetupGuideScreen,
  SupportScreen,
  TicketsScreen,
  TransfersScreen,
  UpcomingSalesScreen,
  WaitlistScreen,
} from './screens';

/** v1's `hasAccess`, unchanged. */
function hasAccess(ent: FlashtoolsEntitlement | undefined): boolean {
  if (!ent) return false;
  return Boolean(ent.comped || ent.tier);
}


/**
 * Everything under `/v2/app`. A structural mirror of v1's `MainLayout`:
 *
 * - the same `useCrowdVoltStatus()` prefetch on mount, so every screen reads
 *   CrowdVolt status from cache exactly as it does in v1;
 * - the same no-subscription redirect, against `V2_BILLING_BYPASS_PATHS` (v1's
 *   `BILLING_BYPASS_PATHS` on the `/v2/app` prefix);
 * - the same `FeatureGate` strings on the same two routes.
 *
 * Route paths are relative: this component is mounted at `/v2/app/*`.
 */
export const NewUiApp: React.FC = () => {
  const location = useLocation();
  // Prefetch CV status once on mount — all screens read from cache.
  useCrowdVoltStatus();
  const {
    data: entitlement,
    isLoading: entitlementLoading,
    isError: entitlementFailed,
    isFetching: entitlementFetching,
    refetch: refetchEntitlement,
  } = useEntitlement();
  const { isSeat, isLoading: seatLoading } = useSeatSession();

  // Anyone without an active sub (and no admin comp) is kicked to billing,
  // except when they're already on billing or a payment-method flow page.
  //
  // A team member never trips this: their entitlement response carries the OWNER's
  // tier, so `hasAccess` is true for them exactly when it is true for the
  // account they are working in. Sending a team member to a billing page they are
  // forbidden from loading would be a dead end.
  //
  // Only an ANSWER of "no subscription" redirects. A request that failed with
  // no cached entitlement (a 429, a dropped connection) is not that answer,
  // and treating it as one looped: redirect → billing screen mounts →
  // its entitlement observer refetches → the query is pending again → the
  // routes come back → the refetch fails → redirect. Six requests a cycle,
  // one cycle a second, for as long as the tab was open (2026-09-18).
  const entitlementUnavailable = entitlementFailed && !entitlement;
  const requiresSubscription =
    !entitlementLoading && !entitlementUnavailable && !hasAccess(entitlement);
  const shouldRedirectToBilling =
    requiresSubscription &&
    !V2_BILLING_BYPASS_PATHS.some((p) => location.pathname.startsWith(p));

  // A team member typing or bookmarking a path they cannot use lands on Tickets
  // rather than on an empty screen or a 403 toast. The nav already hides these;
  // this covers the deep link. Enforcement is still the backend's — every route
  // below is behind endpoints that refuse a team member without the capability.
  const seatOffLimits = !seatLoading && isSeat && !isSeatVisiblePath(location.pathname);

  return (
    <AppShell>
      {entitlementUnavailable ? (
        <EmptyState
          tone="error"
          title="We couldn't load your account"
          body="This is usually temporary. If it keeps happening after a minute, tell us through Support."
          action={
            <Button onClick={() => refetchEntitlement()} disabled={entitlementFetching}>
              {entitlementFetching ? 'Retrying…' : 'Try again'}
            </Button>
          }
        />
      ) : shouldRedirectToBilling ? (
        <Navigate to={v2Path('/billing')} replace />
      ) : seatOffLimits ? (
        <Navigate to={SEAT_LANDING_ROUTE} replace />
      ) : (
        <Routes>
          <Route index element={<Navigate to={V2_DEFAULT_ROUTE} replace />} />
          <Route path="notifications" element={<NotificationsScreen />} />
          {/* Ungated: the endpoint needs only flashtools access, which every
              account reaching this shell already has. */}
          <Route path="upcoming-sales" element={<UpcomingSalesScreen />} />
          <Route
            path="waitlist"
            element={
              <V2FeatureGate
                feature={WAITLIST_GATE.feature}
                title={WAITLIST_GATE.title}
                description={WAITLIST_GATE.description}
                requiredTier={WAITLIST_GATE.requiredTier}
              >
                <WaitlistScreen />
              </V2FeatureGate>
            }
          />
          <Route
            path="drops"
            element={
              <V2FeatureGate
                feature={DROPS_GATE.feature}
                title={DROPS_GATE.title}
                description={DROPS_GATE.description}
                requiredTier={DROPS_GATE.requiredTier}
              >
                <DropsScreen />
              </V2FeatureGate>
            }
          />
          <Route path="tickets" element={<TicketsScreen />} />
          <Route path="listings" element={<ListingsScreen />} />
          <Route path="transfers" element={<TransfersScreen />} />
          <Route path="accounts" element={<AccountsScreen />} />
          <Route path="dice/settings" element={<DiceSettingsScreen />} />
          <Route path="dice/signup" element={<DiceSignupScreen />} />
          <Route path="dice/success" element={<DiceSuccessScreen />} />
          <Route path="profile" element={<ProfileScreen />} />
          <Route path="billing" element={<BillingScreen />} />
          <Route path="seats" element={<SeatsScreen />} />
          <Route path="setup" element={<SetupGuideScreen />} />
          <Route path="support" element={<SupportScreen />} />
          <Route path="*" element={<Navigate to={V2_DEFAULT_ROUTE} replace />} />
        </Routes>
      )}
    </AppShell>
  );
};
